import { Place, Person, EmailData } from '../types/results.types';
import { placeHasCatchAllBusinessDomain } from './catchAllDetection';
import { isVerifiable } from './emailVerifiable';
import { hasUsableBusinessWebsite } from './placeWebsite';

export type RowActionState = 'find' | 'verify' | 'both' | 'ready' | 'generic_only' | 'empty';

export function isReadyToContact(email: EmailData): boolean {
  if (email.is_catch_all === true) return false;
  if (email.verification_status === 'GUARANTEED') return true;
  if (email.verification_status === 'GENERIC') return true;
  return false;
}

function collectAllEmails(place: Place): EmailData[] {
  return [
    ...(place.company_emails ?? []),
    ...(place.people ?? []).flatMap((person) => person.emails ?? []),
  ];
}

function personHasReadyEmail(person: Person): boolean {
  return (person.emails ?? []).some(isReadyToContact);
}

function isManualFindEligible(person: Person): boolean {
  if (person.is_unlocked === true) return false;
  if (person.email_access_state === 'gated_upgrade') return false;
  if (person.email_access_state === 'finding') return false;
  if (person.email_access_state === 'auto_no_email') return false;
  return !personHasReadyEmail(person);
}

export function isFindLive(place: Place): boolean {
  if (!place.people || place.people.length === 0) return false;
  if (!hasUsableBusinessWebsite(place)) return false;
  if (placeHasCatchAllBusinessDomain(place)) return false;
  return place.people.some(isManualFindEligible);
}

export function findableCount(place: Place): number {
  if (!place.people) return 0;
  if (!hasUsableBusinessWebsite(place)) return 0;
  if (placeHasCatchAllBusinessDomain(place)) return 0;
  return place.people.filter(isManualFindEligible).length;
}

export function isVerifyLive(place: Place): boolean {
  return collectAllEmails(place).some(isVerifiable);
}

export function verifiableCount(place: Place): number {
  return collectAllEmails(place).filter(isVerifiable).length;
}

function hasReadyToContactEmail(place: Place): boolean {
  return collectAllEmails(place).some(isReadyToContact);
}

function hasOnlyNonReadyEmails(place: Place): boolean {
  const allEmails = collectAllEmails(place);
  if (allEmails.length === 0) return false;
  return allEmails.every((email) => !isReadyToContact(email));
}

export function resolveRowState(place: Place): RowActionState {
  const findLive = isFindLive(place);
  const verifyLive = isVerifyLive(place);

  if (findLive && verifyLive) return 'both';
  if (findLive) return 'find';
  if (verifyLive) return 'verify';
  if (hasReadyToContactEmail(place)) return 'ready';
  if (hasOnlyNonReadyEmails(place)) return 'generic_only';
  return 'empty';
}

export function findablePeopleIds(place: Place): number[] {
  if (!place.people) return [];
  if (!hasUsableBusinessWebsite(place)) return [];
  if (placeHasCatchAllBusinessDomain(place)) return [];
  return place.people
    .filter(isManualFindEligible)
    .map((person) => person.id);
}

export function verifiableEmailIds(place: Place): number[] {
  return collectAllEmails(place)
    .filter(
      (email): email is EmailData & { id: number } =>
        email.id !== undefined && isVerifiable(email),
    )
    .map((email) => email.id);
}

export function readyCount(place: Place): number {
  return collectAllEmails(place).filter(isReadyToContact).length;
}
